import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useConfig from "../../../actions/Tenants/config/configHook";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { toast } from "../../../utils/toast";
import { getAllTenantFieldOptions } from "../../../actions/Tenants/tenantFieldOptionsActions";
import { exportDataToExcel } from "./utils";
import { UseImportExportProps, UseImportReturn } from "./types";

const headersByEntity = {
    client: ["name", "countries", "fruit_types", "report_type", "emails"],
    supplier: ["name", "country", "fruit_types", "report_type", "emails", "ggn"],
    grower: ["name", "country", "fruit_types", "ggn"],
};

export const useImport = ({
    entityName,
    importFields,
    affectedQueries = [],
    customEndpoint,
    validateImportItems,
    uniqueIdentifierKey = "name"
}: UseImportExportProps): UseImportReturn => {
    const dispatch = useDispatch();
    const config = useConfig();
    const queryClient = useQueryClient();

    const [importData, setImportData] = useState<any[] | null>(null);

    const endpoint = customEndpoint || `/${entityName}/`;
    const queryKey = `all-${entityName}s`;
    const allItems = queryClient.getQueryData<any>([queryKey]);
    const existingItems = allItems?.results?.map((item) => (typeof uniqueIdentifierKey === "string" ? item[uniqueIdentifierKey] : uniqueIdentifierKey?.map((k) => item[k]))) || [];

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: [queryKey], exact: false });
        queryClient.invalidateQueries({ queryKey: [`${entityName}s`], exact: false });
        affectedQueries.forEach((q) => {
            queryClient.invalidateQueries({ queryKey: [q], exact: false });
        });
    };

    const { mutate: importItems } = useMutation({
        mutationFn: async (data: any[]) => {
            // Ensure proper URL construction with slash before "import"
            const importEndpoint = endpoint.endsWith("/") ? `${endpoint}import` : `${endpoint}/import`;
            const res = await callAvosApi(importEndpoint, {
                method: "POST",
                data,
            });

            if (res.data) {
                const createdCount = res.data.created_count || 0;
                const updatedCount = res.data.updated_count || 0;

                if (createdCount || updatedCount) {
                    toast.success(
                        `${(createdCount ? `${createdCount} ${entityName}s created` : "")
                        + (createdCount && updatedCount ? " and " : "")
                        + (updatedCount ? `${updatedCount} ${entityName}s updated` : "")
                        } successfully.`
                    );
                }
            }
        },
        onSuccess: () => {
            invalidateQueries();
            dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
        },
        onError: (error: any) => {
            toast.error(error?.message || `An error occurred while importing ${entityName}s.`);
        }
    });

    const { validItems, errors } = useMemo(() => {
        let result = { validItems: [] as any[], errors: {} as Record<number, Record<string, string>> };

        if (validateImportItems) {
            result = validateImportItems({ importData, existingItems });
        } else {
            const seenKeys: string[] = [];
            importData?.forEach((item, index) => {
                const itemKey = typeof uniqueIdentifierKey === "string"
                    ? item[uniqueIdentifierKey]
                    : JSON.stringify(uniqueIdentifierKey.map((k) => item[k]));
                const fieldErrors: Record<string, string> = {};

                if (seenKeys.includes(itemKey)) {
                    fieldErrors._duplicate = "Duplicate entry";
                }

                importFields.forEach((f) => {
                    if (f.required && !item[f.name!]) {
                        fieldErrors[f.name ?? f.label] = `${f.label ?? f.name} is required`;
                    }
                    if (f.validate && !f.validate(item[f.name!])) {
                        fieldErrors[f.name ?? f.label] = `${f.label ?? f.name} is invalid`;
                    }
                });

                if (Object.keys(fieldErrors).length > 0) {
                    result.errors[index] = fieldErrors;
                } else {
                    // Apply field formatters (e.g. stringify numeric Excel values)
                    const formattedItem = { ...item };
                    importFields.forEach((f) => {
                        if (f.formatter && f.name && formattedItem[f.name] != null) {
                            formattedItem[f.name] = f.formatter(formattedItem[f.name]);
                        }
                    });
                    result.validItems.push(formattedItem);
                    seenKeys.push(itemKey);
                }
            });
        }

        return result;
    }, [importData, existingItems, config]);

    const onImportData = () => {
        importItems(validItems);
    };

    const onDownloadTemplate = () => {
        const templateData = [
            importFields.reduce((acc, curr) => {
                if (curr.copyValueFrom) return acc;
                acc[curr.name ?? curr.label] = "";
                return acc;
            }, {} as any)
        ];

        exportDataToExcel(templateData, `${entityName}_template`, undefined, headersByEntity[entityName]);
    };

    return {
        onDownloadTemplate,
        onImportData,
        setImportData,
        validItems,
        errors,
    };
};
